
import { defineComponent, PropType } from "vue";
import {
  isPGIQualified,
  isPGITop20Qualified,
  vGetCurrentRank,
} from "@/api/dashboard";
import DashboardMoveControls from "@/components/dashboard/DashboardMoveControls.vue";

type DashboardTabRequirement = "pgi" | "pgiTop20" | "nonGa";

export interface DashboardTab {
  to: string;
  label: string;
  requires?: DashboardTabRequirement;
}

const NON_GA_RANKS = ["GA", "GM", "RB", "DM"];

export const OVI_DASHBOARD_TABS: DashboardTab[] = [
  { to: "/dashboard/reminderspage", label: "Reminders" },
  { to: "/dashboard/accountinfo", label: "Account Info" },
  { to: "/dashboard/recentactivity", label: "Recent Activity" },
  { to: "/dashboard/globalpartners", label: "Global Partners" },
  { to: "/dashboard/primemonitoring", label: "Prime Monitoring" },
  { to: "/dashboard/mycoverage", label: "My Coverage" },
  { to: "/dashboard/coveragelist", label: "Available Coverage" },
  { to: "/dashboard/packagepointslist", label: "Package Points" },
];

export const ECP_DASHBOARD_TABS: DashboardTab[] = [
  { to: "/dashboardecp/reminderspage", label: "Reminders" },
  { to: "/dashboardecp/accountinfo", label: "Account Info" },
  { to: "/dashboardecp/recentactivity", label: "Recent Activity" },
  { to: "/dashboardecp/globalpartners", label: "Global Partners" },
  { to: "/dashboardecp/primemonitoring", label: "Prime Monitoring" },
  { to: "/dashboardecp/packagepointslistecp", label: "Package Points" },
  { to: "/dashboardecp/pgiecp", label: "PGI", requires: "pgi" },
  { to: "/dashboardecp/pgiecptop20", label: "PGI Packages", requires: "pgiTop20" },
  { to: "/dashboardecp/nonga", label: "Non-GA Tree", requires: "nonGa" },
];

export default defineComponent({
  name: "DashboardTabNavigator",
  components: {
    DashboardMoveControls,
  },
  props: {
    tabs: {
      type: Array as PropType<DashboardTab[] | null>,
      default: null,
    },
    currentRank: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isPGIQualified: false,
      isPGITop20Qualified: false,
      resolvedCurrentRank: "",
    };
  },
  computed: {
    resolvedTabs(): DashboardTab[] {
      if (this.tabs?.length) {
        return this.tabs;
      }

      const path = String(this.$route?.path || "");
      if (path.startsWith("/dashboardecp")) {
        return ECP_DASHBOARD_TABS;
      }

      return OVI_DASHBOARD_TABS;
    },
    effectiveCurrentRank(): string {
      return String(this.currentRank || this.resolvedCurrentRank || "").trim();
    },
    visibleTabs(): DashboardTab[] {
      return this.resolvedTabs.filter((tab) => this.isTabVisible(tab));
    },
  },
  mounted() {
    this.loadConditionalTabFlags();
  },
  methods: {
    isTabVisible(tab: DashboardTab): boolean {
      if (!tab.requires) {
        return true;
      }

      if (tab.requires === "pgi") {
        return this.isPGIQualified;
      }

      if (tab.requires === "pgiTop20") {
        return this.isPGITop20Qualified;
      }

      if (tab.requires === "nonGa") {
        return NON_GA_RANKS.includes(this.effectiveCurrentRank);
      }

      return true;
    },
    async loadConditionalTabFlags() {
      const requirements = new Set(
        this.resolvedTabs
          .map((tab) => tab.requires)
          .filter((req): req is DashboardTabRequirement => Boolean(req))
      );

      if (!requirements.size) {
        return;
      }

      const tasks: Promise<void>[] = [];

      if (requirements.has("pgi")) {
        tasks.push(this.checkIfPGIQualified());
      }

      if (requirements.has("pgiTop20")) {
        tasks.push(this.checkIfPGITop20Qualified());
      }

      if (requirements.has("nonGa") && !this.currentRank) {
        tasks.push(this.loadCurrentRank());
      }

      await Promise.all(tasks);
    },
    async checkIfPGIQualified() {
      try {
        const response = await isPGIQualified();
        this.isPGIQualified = Boolean(response?.data?.isPGIQualified);
      } catch (error) {
        console.error("Failed to load PGI qualification:", error);
      }
    },
    async checkIfPGITop20Qualified() {
      try {
        const response = await isPGITop20Qualified();
        this.isPGITop20Qualified = Boolean(response?.data?.isPGITop20Qualified);
      } catch (error) {
        console.error("Failed to load PGI Packages qualification:", error);
      }
    },
    async loadCurrentRank() {
      try {
        const response = await vGetCurrentRank();
        this.resolvedCurrentRank = String(response?.data?.currentRank || "");
      } catch (error) {
        console.error("Failed to load current rank for tabs:", error);
      }
    },
  },
});
